import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import FlightTakeoffOutlinedIcon from '@mui/icons-material/FlightTakeoffOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';

interface Step {
    number: number;
    icon: React.ReactNode;
    title: string;
    description: string;
    link?: string;
}

const HowItWorksSection: React.FC = () => {
  const { isEnabled } = useFeatureFlags();
  const regOpen = isEnabled('registration_open');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    const steps: Step[] = [
        {
            number: 1,
            icon: <AddCircleOutlineIcon sx={{ fontSize: '2.5rem' }} />,
            title: 'Document Your Trip',
            description: 'Start from scratch or import an existing booking. Just upload your confirmation PDF and we\'ll do the rest.',
        },
        {
            number: 2,
            icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: '2.5rem' }} />,
            title: 'Invite Your Circle',
            description: 'Add trip companions who can view your itinerary and share photos. Control visibility — public, friends & family, or private.',
        },
        {
            number: 3,
            icon: <FlightTakeoffOutlinedIcon sx={{ fontSize: '2.5rem' }} />,
            title: 'Share & Discover',
            description: 'Share your trip with your circle or the Percoro community. Discover real trips from real travelers for authentic inspiration.',
        },
    ];

    const handleCardClick = (link?: string) => {
        if (link) {
            navigate(link);
        }
    };

    return (
        <Box
            sx={{
                py: { xs: 8, md: 12 },
                background: theme.palette.background.default,
            }}
        >
            <Container maxWidth="lg">
                {/* Section Header */}
                <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: '2rem', md: '2.75rem' },
                            fontWeight: 'bold',
                            color: 'text.primary',
                            mb: 2,
                        }}
                    >
                        How It Works
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'text.secondary',
                            maxWidth: '600px',
                            mx: 'auto',
                            fontSize: { xs: '1rem', md: '1.15rem' },
                            lineHeight: 1.6,
                        }}
                    >
                        Get started in three simple steps.
                    </Typography>
                </Box>

                {/* Steps Grid */}
                <Grid container spacing={4} justifyContent="center">
                    {steps.map((step, index) => (
                        <Grid item xs={12} md={4} key={step.number}>
                            <Card
                                onClick={() => handleCardClick(step.link)}
                                sx={{
                                    height: '100%',
                                    position: 'relative',
                                    borderRadius: '20px',
                                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                                    transition: 'all 0.3s ease',
                                    cursor: step.link ? 'pointer' : 'default',
                                    overflow: 'visible',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                                    },
                                }}
                            >
                                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                    {/* Step Number Badge - TOP RIGHT */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '-12px',
                                            right: '-12px',
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '1.1rem',
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                                        }}
                                    >
                                        {step.number}
                                    </Box>

                                    {/* Icon - OUTLINE STYLE with border */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '80px',
                                            height: '80px',
                                            background: 'transparent',
                                            border: `2px solid ${theme.palette.primary.main}`,
                                            borderRadius: '16px',
                                            mb: 3,
                                            color: theme.palette.primary.main,
                                            mx: 'auto',
                                        }}
                                    >
                                        {step.icon}
                                    </Box>

                                    {/* Title */}
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 'bold',
                                            color: 'text.primary',
                                            mb: 1.5,
                                            textAlign: 'center',
                                            fontSize: { xs: '1.1rem', md: '1.2rem' },
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 0.5,
                                        }}
                                    >
                                        {step.title}
                                        {step.link && (
                                            <ArrowForwardIcon sx={{ fontSize: '1.2rem', color: theme.palette.primary.main }} />
                                        )}
                                    </Typography>

                                    {/* Description */}
                                    <Typography
                                        sx={{
                                            color: 'text.secondary',
                                            lineHeight: 1.7,
                                            fontSize: '0.95rem',
                                        }}
                                    >
                                        {step.description}
                                    </Typography>
                                </CardContent>
                            </Card>

                            {/* Connector Arrow (between cards on desktop) */}
                            {!isMobile && index < steps.length - 1 && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        right: '-32px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: theme.palette.primary.main,
                                        opacity: 0.6,
                                        display: { xs: 'none', md: 'block' },
                                    }}
                                >
                                    <ArrowForwardIcon sx={{ fontSize: '2rem' }} />
                                </Box>
                            )}
                        </Grid>
                    ))}
                </Grid>

                {/* Bottom CTA Banner */}
                <Box
                    onClick={() => navigate(regOpen ? '/signup' : '/early-access')}
                    sx={{
                        mt: 8,
                        p: 4,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        borderRadius: '20px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
                        },
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            color: 'white',
                            fontWeight: 'bold',
                            mb: 1,
                        }}
                    >
                        Ready to Start Your Journey?
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '1.1rem',
                        }}
                    >
                        Join our community today → Click to Sign Up
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default HowItWorksSection;
